import { Component, OnInit, ViewChild, signal, OnDestroy, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { MenuItem } from 'primeng/api';
import { MessageService, ConfirmationService, SortMeta } from 'primeng/api';
import { ClienteService } from '../../../services/cliente/cliente.service';
import { Cliente } from '../../../domain/cliente.domain';
import { Subject, takeUntil } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { AnuncianteLinkDialogComponent } from '../../../components/clientes/anunciante-link-dialog/anunciante-link-dialog.component';
import { DialogModule } from 'primeng/dialog';

interface Column {
  field: string;
  header: string;
  fieldType?: 'text' | 'boolean' | 'boolean-icon' | 'number' | 'date';
  sortable?: boolean;
}

@Component({
  selector: 'app-cliente-anunciantes',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    RippleModule,
    TooltipModule,
    ConfirmDialogModule,
    BreadcrumbModule,
    CardModule,
    TagModule,
    AnuncianteLinkDialogComponent,
    DialogModule
  ],
  providers: [ConfirmationService],
  templateUrl: './cliente-anunciantes.component.html',
  styleUrls: ['./cliente-anunciantes.component.scss']
})
export class ClienteAnunciantesComponent implements OnInit, OnDestroy {
  @ViewChild('dt') table: Table | undefined;

  private clienteService = inject(ClienteService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);

  // Signals
  cliente = signal<Cliente | null>(null);
  anunciantes = signal<Cliente[]>([]);
  loading = signal<boolean>(false);
  totalRecords = signal<number>(0);
  showLinkDialog = signal<boolean>(false);
  breadcrumbItems = signal<MenuItem[]>([]);

  // Configuración
  nroFilasPorPagina = 10;
  paginaActual = 0;
  filterValue: string = '';

  // Control de suscripciones
  private destroy$ = new Subject<void>();

  // Columnas
  columnsTable: Column[] = [
    { field: 'razonSocial', header: 'Razón Social', fieldType: 'text', sortable: true },
    { field: 'cuit', header: 'CUIT', fieldType: 'text', sortable: true },
    { field: 'email', header: 'Email', fieldType: 'text', sortable: true },
    { field: 'telefono', header: 'Teléfono', fieldType: 'text', sortable: true },
    { field: 'active', header: 'Estado', fieldType: 'boolean-icon', sortable: true },
    { field: 'accion', header: 'Acciones', sortable: false }
  ];

  multiSortMeta: SortMeta[] = [];
  filtersMetadata: any = {};

  ngOnInit(): void {
    const clienteId = this.route.snapshot.paramMap.get('id');
    if (clienteId) {
      this.loadCliente(clienteId);
    } else {
      this.router.navigate(['/clientes']);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadCliente(id: string): void {
    this.loading.set(true);
    this.clienteService.getById(id).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (cliente: Cliente) => {
        this.cliente.set(cliente);
        this.updateBreadcrumb(cliente);
        this.loading.set(false);
        // Cargar anunciantes después de que el cliente esté cargado
        this.loadAnunciantes();
      },
      error: (error) => {
        console.error('Error al cargar cliente:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar los datos del cliente',
          life: 3000
        });
        this.loading.set(false);
        this.router.navigate(['/clientes']);
      }
    });
  }

  loadAnunciantes(event?: TableLazyLoadEvent): void {
    const cliente = this.cliente();
    if (!cliente?.id) {
      return;
    }

    this.loading.set(true);

    // Calcular página
    const first = event?.first ?? 0;
    const page = Math.floor(first / this.nroFilasPorPagina) + 1;
    const size = event?.rows ?? this.nroFilasPorPagina;

    // Construir parámetros
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('tipoCliente', 'anunciante')
      .set('agenciaProductor', cliente.id.toString());

    // Filtro global
    if (this.filterValue?.trim()) {
      params = params.set('q', this.filterValue.trim());
    }

    // Ordenamiento
    if (event?.multiSortMeta && event.multiSortMeta.length > 0) {
      event.multiSortMeta.forEach((sort: SortMeta) => {
        const direction = sort.order === -1 ? 'desc' : 'asc';
        params = params.set(`_order[${sort.field}]`, direction);
      });
    } else {
      // Orden por defecto
      params = params.set('_order[razonSocial]', 'asc');
    }

    this.clienteService.searchClientesCollection(params).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.anunciantes.set(response.members);
        this.totalRecords.set(response.totalItems);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error al cargar anunciantes:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar los anunciantes',
          life: 3000
        });
        this.loading.set(false);
      }
    });
  }

  onGlobalFilter(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.filterValue = value;
    // Recargar la tabla con el nuevo filtro usando lazy load
    const lazyEvent: TableLazyLoadEvent = {
      first: 0,
      rows: this.nroFilasPorPagina,
      sortField: undefined,
      sortOrder: undefined,
      filters: {},
      multiSortMeta: this.multiSortMeta
    };
    this.loadAnunciantes(lazyEvent);
    // Resetear la tabla si está disponible
    if (this.table) {
      this.table.reset();
    }
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    this.loadAnunciantes(event);
  }

  openLinkDialog(): void {
    this.showLinkDialog.set(true);
  }

  onAnuncianteLinked(): void {
    this.showLinkDialog.set(false);
    this.loadAnunciantes();
    this.messageService.add({
      severity: 'success',
      summary: 'Éxito',
      detail: 'Anunciante vinculado correctamente',
      life: 3000
    });
  }

  unlinkAnunciante(anunciante: Cliente): void {
    if (!anunciante.id) {
      return;
    }

    this.confirmationService.confirm({
      message: `¿Está seguro que desea desvincular a "${anunciante.razonSocial}"?`,
      header: 'Confirmar Desvinculación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, desvincular',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.loading.set(true);
        this.clienteService.unlinkAnunciante(anunciante.id!).pipe(
          takeUntil(this.destroy$)
        ).subscribe({
          next: () => {
            this.loading.set(false);
            this.messageService.add({
              severity: 'success',
              summary: 'Éxito',
              detail: 'Anunciante desvinculado correctamente',
              life: 3000
            });
            this.loadAnunciantes();
          },
          error: (error) => {
            console.error('Error al desvincular anunciante:', error);
            this.loading.set(false);
            let errorMessage = 'Error al desvincular el anunciante';
            if (error.error?.detail) {
              errorMessage = error.error.detail;
            } else if (error.error?.message) {
              errorMessage = error.error.message;
            }
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: errorMessage,
              life: 5000
            });
          }
        });
      }
    });
  }

  editAnunciante(anunciante: Cliente): void {
    if (anunciante.id) {
      this.router.navigate(['/clientes/edit', anunciante.id]);
    }
  }

  goBack(): void {
    const cliente = this.cliente();
    if (cliente?.id) {
      this.router.navigate(['/clientes/edit', cliente.id]);
    } else {
      this.router.navigate(['/clientes']);
    }
  }

  getTipoClienteLabel(tipoCliente?: string[]): string {
    if (!tipoCliente || tipoCliente.length === 0) {
      return '';
    }
    const labels: { [key: string]: string } = {
      'anunciante': 'Anunciante',
      'agencia': 'Agencia',
      'productor': 'Productor'
    };
    return tipoCliente.map(t => labels[t] || t).join(', ');
  }

  updateBreadcrumb(cliente: Cliente): void {
    const items: MenuItem[] = [
      {
        label: 'Clientes',
        routerLink: '/clientes'
      },
      {
        label: cliente.razonSocial || 'Cliente',
        routerLink: cliente.id ? ['/clientes/edit', cliente.id] : null
      },
      {
        label: 'Anunciantes'
      }
    ];
    this.breadcrumbItems.set(items);
  }
}

